import {
  fetchBiodirect,
  fetchClinicStudies,
  fetchCompletedClinicStudies,
  fetchDeletedStudies,
  fetchEventCounts,
  fetchFlaggedStudies,
  fetchLastEvaluationEvent,
  fetchUnassignedStudies,
  handleAssignDeviceToStudy,
  handleCancelStudy,
  handleGetEvaluationEvent,
  handleRestoreStudy,
  handleSendStudyReferenceCode,
  handleStopStudy,
  studyNavigationState,
  calculateStudyProgress,
  STUDY_STATUS,
  PENDING_COMMAND_EXISTS_MESSAGE,
  STUDY_PENDING_COMMAND,
  checkHasSentRefCode,
  getSendRefCodeSuccessfullyMessage,
  getSendRefCodeUnsuccessfullyMessage,
} from '@/entities/study';
import { DEVICE, DOUBLE_HYPHEN_BULLET, EMITTER_CONSTANTS } from '@/shared/config';
import {
  checkIsChangedSorter,
  zeroPad,
  auth,
  emitter,
  generateFullName,
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
  dateToString,
  getStudyDuration,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_STUDIES,
  appearDataTable,
  preventAppScroll,
} from '@/shared/lib';
import { useEmitter, useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import type {
  BiodirectEventSocketMessage,
  DeviceEventSocketMessage,
  DeviceStartedStudySocketMessage,
  LastSyncSocketMessage,
  NewEvaluationStripSocketMessage,
  StudyDeletedSocketMessage,
  StudyEventSocketMessage,
  StudyUpdatedSocketMessage,
  UploadStatusSockeMessage,
} from '@/shared/lib';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import { userPreferences } from '@/entities/user';
import type {
  FilterObject,
  FormattedStudy,
  StudiesTableProps,
  StudiesTableState,
} from './studiesTable.types';
import { calculateEtaReportDate } from '../../lib/studiesTable';

// The flagged/deleted/unassigned/completed/clinic studies queries each select a different
// subset of Study fields for their own tab, so studiesFormatter's input can only rely on
// the fields it actually reads below, all optional since no single query provides them all.
interface RawStudy {
  id: string;
  status: string;
  friendlyId: number;
  start: string;
  stop: string;
  timezone: string;
  studyTimeOffset?: number;
  initDuration?: number;
  ecgUploadProgress?: number;
  biofluxDirect?: unknown;
  isBiofluxDirect?: boolean;
  uploadStatus?: unknown;
  initiatedTime?: string;
  facility?: {
    contact?: { country?: string };
    timezone?: string;
  };
  device?: { status?: string };
  eventCount?: { totalEvents?: number; openEvents?: number };
  lastEvaluationEvent?: unknown;
  info?: {
    interprettingPhysician?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      contact?: { phone1?: string; nightPhone?: string; fax?: string };
      email?: string;
    };
    referringPhysician?: {
      firstName?: string;
      lastName?: string;
      contact?: { phone1?: string; nightPhone?: string; fax?: string };
      email?: string;
    };
    diagnosis?: {
      primary?: string;
      second?: string;
      patientMedications?: string;
      physiciansNote?: string;
    };
    patient?: {
      firstName?: string;
      lastName?: string;
      gender?: string;
      patientId?: string;
      dob?: string;
      email?: string;
      contact?: {
        country?: string;
        phone1?: string;
        phone2?: string;
        address?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
      secondaryContact?: Record<string, unknown>;
    };
    followOnStudy?: unknown;
  };
}

dayjs.extend(utc);

// StudiesTableState declares these as required FormattedStudy/number/string, but they hold no
// selected study/command until one is picked, so this hook widens them to their nullable reality.
type NullableStudiesTableState = Omit<
  StudiesTableState,
  | 'displayedSolutionStudy'
  | 'displayedDisclaimerModalStudy'
  | 'displayedLiveEcgModalStudy'
  | 'displayedUploadStatusModalStudy'
  | 'shouldAbortStudy'
  | 'displayedResendRefCodeStudy'
  | 'needStopStudyFid'
  | 'pendingCommandToHandle'
  | 'shouldAssignDeviceStudy'
> & {
  displayedSolutionStudy: FormattedStudy | null;
  displayedDisclaimerModalStudy: FormattedStudy | null;
  displayedLiveEcgModalStudy: FormattedStudy | null;
  displayedUploadStatusModalStudy: FormattedStudy | null;
  shouldAbortStudy: FormattedStudy | null;
  displayedResendRefCodeStudy: FormattedStudy | null;
  needStopStudyFid: number | null;
  pendingCommandToHandle: string | null;
  shouldAssignDeviceStudy: FormattedStudy | null;
};

/**
 * Owns the data, filters and actions of one studies list tab, from fetching to socket updates.
 * Seeds the filter from navigation state and props, fetches and paginates studies, refetches on
 * search or facility changes, subscribes to evaluation strip, BioFlux Direct and flagged-study
 * events, refreshes study progress on an interval, and exposes modal, drawer, abort, stop and
 * restore handlers.
 *
 * @param props - Tab props carrying the tab flags, facility selection, search values, checked
 * studies and the count, clear and type inputs.
 * @returns The refs, state and setter, formatters, fetch, socket and UI handlers the studies table
 * renders with.
 */
export const useStudiesTable = ({
  isDeleted,
  isCompleted,
  isEndOfDuration,
  isOngoing,
  isUnassigned,
  isFlaggedStudiesTab,
  selectedFacility,
  selectedTypeSeePatients,
  handleFetchOngoingCount,
  isActive,
  supportFacilities,
  checkedStudyIds,
  searchValue: searchValueProp,
  searchByValue: searchByValueProp,
  multiStudyFid: multiStudyFidProp,
  studyType,
  handleClickClear,
  type,
}: Pick<
  StudiesTableProps,
  | 'isDeleted'
  | 'isCompleted'
  | 'isEndOfDuration'
  | 'isOngoing'
  | 'isUnassigned'
  | 'isFlaggedStudiesTab'
  | 'selectedFacility'
  | 'selectedTypeSeePatients'
  | 'handleFetchOngoingCount'
  | 'isActive'
  | 'supportFacilities'
  | 'checkedStudyIds'
  | 'searchValue'
  | 'searchByValue'
  | 'multiStudyFid'
  | 'studyType'
  | 'handleClickClear'
  | 'type'
>) => {
  const { state: locationState } = useLocation();

  const navigate = useNavigate();

  const updateActiveStudyProgressIntervalFunc = React.useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const hasChangedSearchValue = useRef(false);
  const filterObject = useRef<FilterObject>({
    studyFid:
      studyNavigationState.search.searchByValue === 'studyFid'
        ? studyNavigationState.search.searchValue
        : undefined,
    studyFids: undefined,
    patientName:
      studyNavigationState.search.searchByValue === 'patientName'
        ? studyNavigationState.search.searchValue
        : undefined,
    patientId:
      studyNavigationState.search.searchByValue === 'patientId'
        ? _.trim(studyNavigationState.search.searchValue)
        : undefined,
    deviceId: _.isEmpty(locationState?.deviceId)
      ? studyNavigationState.search.searchByValue === 'deviceId'
        ? _.trim(studyNavigationState.search.searchValue)
        : undefined
      : locationState?.deviceId,
    status: isDeleted || isEndOfDuration ? undefined : type,
    includeRma: true,
    sortOrder: isFlaggedStudiesTab ? 'asc' : 'desc',
    cursor: undefined,
    facilityId: undefined,
    facilityIds: undefined,
  });
  const needFetchMoreStudiesNextPage = useRef(false);
  const isPreventFetchMore = useRef(false);
  const searchByValue = useRef(
    searchByValueProp || (locationState?.deviceId ? 'deviceId' : 'patientName'),
  );
  const searchValue = useRef(searchValueProp || '');
  const multiStudyFid = useRef(multiStudyFidProp || []);
  const lastQueryFilter = useRef({});
  const cursorObject = useRef({
    cursor: undefined,
  });
  const flaggedStudiesPage = useRef(0);
  const viewLiveEcgModalRef = useRef<{ resetPause: () => void } | null>(null);
  const studyCardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const skipSelfUpdateFlaggedStudies = useRef<string[]>([]);

  const [state, setState] = useSetState<NullableStudiesTableState>({
    skeletonStudies: _.map(_.range(10), (item) => ({
      id: item,
    })),
    studies: [],
    displayedBiodirectStudy: null,
    isLoadedData: !(
      facilitySelection.userSupportFacilities()?.length > 0 || supportFacilities?.length > 0
    ),
    studiesPage: 1,
    sizePerPage: 10,
    maxDisplayedStudies: 10,
    isEndOfData: false,
    isOpenConfirmDeleteStudiesModal: false,
    isDisabledSearchInput: true,
    isConfirmGetECG: null,
    isLoadingMore: false,
    needFetchMoreStudiesCurrentPage: false,
    restoreStudyIds: [],
    isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
    displayedSolutionStudy: null,
    isVisibleCommonQuestionsDrawer: false,
    isOpenNotiPopover: false,
    isCheck: userPreferences.isCheckedNotiCommonQuestions(),
    isCheckedAbortedStudy: studyNavigationState.search.isCheckedAbortedStudy,
    nonSelectedStudyIds: [],
    isOpenDisclaimerModal: false,
    isOpenUploadStatusModal: false,
    displayedDisclaimerModalStudy: null,
    isOpenResendModal: false,
    isOpenTooManyRequestModal: false,
    tooManyRequestMessage: '',
    openLiveEcgModal: false,
    displayedLiveEcgModalStudy: null,
    displayedUploadStatusModalStudy: null,
    isOpenConfirmStopStudyModal: false,
    isOpenUnableStopStudyModal: false,
    openConfirmAbortStudyModal: false,
    shouldAbortStudy: null,
    isCallingAbortStudy: false,
    isStopingStudyModal: false,
    displayedResendRefCodeStudy: null,
    isOpenSolutionsModal: false,
    isChangingFacility: false,
    openConfirmActivityModal: false,
    openStopLiveEcgModal: false,
    isSaving: false,
    needStopStudyFid: null,
    pendingCommandToHandle: null,
    visibleAssignDeviceDrawer: false,
    shouldAssignDeviceStudy: null,
  });

  const hasSentRefCode = checkHasSentRefCode((state.displayedResendRefCodeStudy ?? {}) as any);

  /**
   * Collects the facility ids the signed-in user is allowed to see flagged studies for. Reads the user's support facilities and the selected-facility value from local storage, and only when that value is the all-facilities marker reduces the support facilities down to the ids whose canViewFlaggedStudies flag is set, otherwise yielding undefined.
   *
   * @returns Array of facility ids with flagged-study access, or undefined when a single facility is selected.
   */
  const getCanViewFlaggedStudiesFacilityIds = () => {
    const userSupportFacilities = facilitySelection.userSupportFacilities();
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    if (selectedFacilityLocalStorage.value === GETTEXT_ALL_FACILITIES) {
      return _.reduce(
        userSupportFacilities,
        (canViewFlaggedStudiesFacilityIds, supportFacility) => {
          if (supportFacility.canViewFlaggedStudies) {
            canViewFlaggedStudiesFacilityIds.push(supportFacility.id);
          }
          return canViewFlaggedStudiesFacilityIds;
        },
        [] as string[],
      );
    }
    return undefined;
  };

  /**
   * Flattens raw study records into the shape the studies table renders. Per study it computes the ETA report date, a tab-keyed action button tag, progress (zero while STARTING or DRAFT) and a duration preferring initDuration for non-started studies on the ongoing, unassigned and flagged tabs, then lifts the nested info payload into flat physician, diagnosis, patient (full name plus joined address) and event-count fields with defaults for facility, device and upload status.
   *
   * @param studies - Raw study records returned by the studies queries.
   * @returns Study rows ready for the table, one per input study.
   */
  const studiesFormatter = (studies: RawStudy[]) => {
    const formattedStudies: FormattedStudy[] = [];
    _.forEach(studies, (study) => {
      const etaReportDate = calculateEtaReportDate(study);
      formattedStudies.push({
        ...study,
        actionButtonTag: `${study.id}-${isCompleted ? 'completed' : isEndOfDuration ? 'end-of-duration' : 'active'}`,
        studyFid: study.friendlyId,
        etaReportDate,
        progress: [STUDY_STATUS.STARTING, STUDY_STATUS.DRAFT].includes(study.status)
          ? 0
          : calculateStudyProgress(study),
        duration:
          isOngoing || isUnassigned || isFlaggedStudiesTab
            ? study.status === STUDY_STATUS.STARTED
              ? getStudyDuration(study.start, study.stop)
              : study?.initDuration
            : getStudyDuration(study.start, study.stop),
        interprettingPhysician: {
          id: study?.info?.interprettingPhysician?.id,
          name: generateFullName(study?.info?.interprettingPhysician),
          contact: {
            phone1: study?.info?.interprettingPhysician?.contact?.phone1,
            nightPhone: study?.info?.interprettingPhysician?.contact?.nightPhone,
            fax: study?.info?.interprettingPhysician?.contact?.fax,
          },
          email: study?.info?.interprettingPhysician?.email,
        },
        diagnosis: {
          primary: study?.info?.diagnosis?.primary,
          second: study?.info?.diagnosis?.second,
          patientMedications: study?.info?.diagnosis?.patientMedications,
        },
        physiciansNote: study?.info?.diagnosis?.physiciansNote,
        patient: {
          name: generateFullName(study?.info?.patient),
          gender: study?.info?.patient?.gender,
          patientId: study?.info?.patient?.patientId,
          dob: study?.info?.patient?.dob,
          email: study?.info?.patient?.email,
          contact: {
            country: study?.info?.patient?.contact?.country,
            phone1: study?.info?.patient?.contact?.phone1,
            phone2: study?.info?.patient?.contact?.phone2,
            address: _.join(
              _.filter(
                [
                  study?.info?.patient?.contact?.address,
                  study?.info?.patient?.contact?.city,
                  study?.info?.patient?.contact?.state,
                  study?.info?.patient?.contact?.zip,
                ],
                Boolean,
              ),
              ', ',
            ),
          },
          secondaryContact: {
            ...study?.info?.patient?.secondaryContact,
          },
        },
        referringPhysician: {
          name: generateFullName(study?.info?.referringPhysician),
          contact: {
            phone1: study?.info?.referringPhysician?.contact?.phone1,
            nightPhone: study?.info?.referringPhysician?.contact?.nightPhone,
            fax: study?.info?.referringPhysician?.contact?.fax,
          },
          email: study?.info?.referringPhysician?.email,
        },
        ecgUploadProgress: study.ecgUploadProgress || 0,
        facility: study?.facility || {},
        device: study?.device || {},
        deviceStatus: study?.device?.status,
        totalEvents: study?.eventCount?.totalEvents,
        openEvents: study?.eventCount?.openEvents,
        lastEvaluationEvent: study.lastEvaluationEvent || {},
        isBiofluxDirect: !_.isEmpty(study.biofluxDirect) || study.isBiofluxDirect,
        followOnStudy: study.info?.followOnStudy,
        uploadStatus: study.uploadStatus || {},
        initiatedTime:
          dateToString({
            startStudyDate: study.start,
            isoDate: study.initiatedTime,
            timezone: study.timezone,
            studyTimeOffset: study.studyTimeOffset,
            facilityCountry: study.facility?.contact?.country,
            facilityTimezone: study.facility?.timezone,
          }) || DOUBLE_HYPHEN_BULLET,
      } as FormattedStudy);
    });

    return formattedStudies;
  };

  /**
   * Loads a page of studies for the active tab and folds the result into the table state. Guards overlapping fetch-more calls, derives the facility, facilityIds, isArtifact and includeAborted filter fields, queries flagged, deleted, unassigned, completed or clinic studies per tab and formats them, then drops results whose filter changed mid-flight, appends for a cursor page, replaces on refresh with a recalculated page count, or sets outright, updating end-of-data, cursor and loading flags.
   *
   * @param updatedFilterObject - Filter fields to merge over the current query filter.
   * @param isRefresh - Whether to reload the already displayed range instead of paging.
   * @param limit - Explicit page size override for this request.
   * @param shouldDisplayInfoMessage - Whether to show the no-more-studies notice on an empty cursor page.
   * @returns Promise that settles once the studies state has been updated.
   */
  const fetchData = async ({
    updatedFilterObject = {},
    isRefresh = false,
    limit,
    shouldDisplayInfoMessage = true,
  }: {
    updatedFilterObject?: FilterObject;
    isRefresh?: boolean;
    limit?: number;
    shouldDisplayInfoMessage?: boolean;
  }) => {
    const isStudyCard = isOngoing || isUnassigned || isFlaggedStudiesTab;

    if (isPreventFetchMore.current && needFetchMoreStudiesNextPage.current && isStudyCard) {
      return;
    }
    isPreventFetchMore.current = true;

    if (needFetchMoreStudiesNextPage.current) {
      setState({ isLoadingMore: true });
      if (isFlaggedStudiesTab) {
        flaggedStudiesPage.current += 1;
      }
    }
    if (!updatedFilterObject.cursor) {
      flaggedStudiesPage.current = 0;
    }

    filterObject.current.facilityId =
      selectedFacility.value !== GETTEXT_ALL_FACILITIES ? selectedFacility.value : undefined;

    if (isFlaggedStudiesTab) {
      filterObject.current.facilityIds =
        selectedFacility.value === GETTEXT_ALL_FACILITIES
          ? getCanViewFlaggedStudiesFacilityIds()
          : undefined;
    }

    filterObject.current.isArtifact = isFlaggedStudiesTab ? true : undefined;

    filterObject.current.includeAborted = state.isCheckedAbortedStudy;

    const queryFilter = { ...filterObject.current, ...updatedFilterObject };

    filterObject.current = { ...queryFilter };

    lastQueryFilter.current = _.cloneDeep(queryFilter);

    try {
      const queryFilterPayload = {
        ...queryFilter,
        studyFid:
          queryFilter?.studyFid?.toString()?.trim() && !_.isNaN(parseInt(queryFilter?.studyFid, 10))
            ? parseInt(queryFilter?.studyFid, 10)
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
      };
      let newStudies = [];
      if (isFlaggedStudiesTab) {
        const flaggedFilter = {
          studyFid: queryFilterPayload?.studyFid || undefined,
          deviceId: queryFilterPayload?.deviceId || undefined,
          patientId: _.trim(queryFilterPayload?.patientId) || undefined,
          patientName: queryFilterPayload?.patientName || undefined,
          facilityIds: queryFilterPayload?.facilityId
            ? [queryFilterPayload?.facilityId]
            : getCanViewFlaggedStudiesFacilityIds(),
        };
        const flaggedStudies = await fetchFlaggedStudies(
          flaggedFilter,
          limit || (isRefresh ? Math.ceil(state.maxDisplayedStudies / 10) * 10 : state.sizePerPage),
          flaggedStudiesPage.current,
        );
        newStudies = studiesFormatter(_.compact(flaggedStudies) as RawStudy[]);
        if (isRefresh) {
          // *: set page base on max displayed studies
          flaggedStudiesPage.current = Math.ceil(state.maxDisplayedStudies / state.sizePerPage) - 1;
        }
      } else if (isDeleted) {
        const deletedStudies = await fetchDeletedStudies(
          queryFilterPayload,
          limit || (isRefresh ? state.maxDisplayedStudies : state.sizePerPage),
        );
        newStudies = studiesFormatter(_.compact(deletedStudies) as RawStudy[]);
      } else {
        const studies = isUnassigned
          ? await fetchUnassignedStudies(
              queryFilterPayload,
              limit || (isRefresh ? state.maxDisplayedStudies : state.sizePerPage),
            )
          : isCompleted
            ? await fetchCompletedClinicStudies(
                queryFilterPayload,
                limit || (isRefresh ? state.maxDisplayedStudies : state.sizePerPage),
              )
            : await fetchClinicStudies(
                queryFilterPayload,
                limit || (isRefresh ? state.maxDisplayedStudies : state.sizePerPage),
              );
        newStudies = studiesFormatter(_.compact([...studies]) as RawStudy[]);
      }

      if (!_.isEqual(queryFilter, lastQueryFilter.current)) {
        setState({
          isLoadedData: true,
          isDisabledSearchInput: false,
        });
        return;
      }

      const setStateObject: any = {};
      _.assign(setStateObject, {
        isEndOfData: newStudies.length < (limit || state.sizePerPage),
        isLoadedData: true,
        isDisabledSearchInput: false,
      });

      if (queryFilter.cursor) {
        const diffStudies = _.differenceBy(newStudies, state.studies, 'id');
        const appearedStudies = appearDataTable(state.studies, newStudies);
        // *: When change size per page
        if (limit) {
          _.assign(setStateObject, {
            studies: appearedStudies,
            maxDisplayedStudies:
              state.sizePerPage * state.studiesPage > state.maxDisplayedStudies
                ? state.sizePerPage * state.studiesPage
                : state.maxDisplayedStudies,
            isEndOfData: newStudies.length < (limit || state.sizePerPage) || !diffStudies.length, // *: New studies is similar to current studies
          });
        } else {
          _.assign(setStateObject, {
            studies: appearedStudies,
            studiesPage: needFetchMoreStudiesNextPage.current
              ? state.studiesPage + 1
              : state.studiesPage,
            maxDisplayedStudies: needFetchMoreStudiesNextPage.current
              ? state.maxDisplayedStudies + state.sizePerPage
              : state.maxDisplayedStudies,
            isEndOfData: newStudies.length < (limit || state.sizePerPage) || !diffStudies.length,
          });
        }

        if (shouldDisplayInfoMessage) {
          if (_.isEmpty(newStudies) && !_.isEmpty(state.studies)) {
            showMessageInfo({ content: t`No more studies` });
          }
        }
      } else if (isRefresh) {
        const needDecreaseStudiesPage = newStudies.length < state.maxDisplayedStudies;
        const newMaxDisplayedStudies = needDecreaseStudiesPage
          ? Math.ceil(newStudies.length / state.sizePerPage)
          : state.maxDisplayedStudies;
        _.assign(setStateObject, {
          studies: newStudies,
          maxDisplayedStudies: newMaxDisplayedStudies,
          studiesPage: newMaxDisplayedStudies / state.sizePerPage,
        });
      } else {
        _.assign(setStateObject, { studies: newStudies });
      }

      if (needFetchMoreStudiesNextPage.current) {
        _.assign(setStateObject, { isLoadingMore: false });
      }

      if (state.needFetchMoreStudiesCurrentPage) {
        _.assign(setStateObject, { needFetchMoreStudiesCurrentPage: false });
      }

      if (setStateObject.studies?.length) {
        _.assign(cursorObject.current, {
          cursor: setStateObject.studies[setStateObject.studies.length - 1].id,
        });
      }

      setState(setStateObject);

      if (isStudyCard) {
        setTimeout(() => {
          isPreventFetchMore.current = false;
        }, 200);
      }
    } catch (error) {
      console.error('Failed to fetch studies: ', error);
      showMessageError({ content: (error as any)?.message });
      setState({
        studies: [],
        isLoadedData: true,
        isDisabledSearchInput: false,
        isLoadingMore: false,
        needFetchMoreStudiesCurrentPage: false,
      });
    }

    if (needFetchMoreStudiesNextPage.current) {
      needFetchMoreStudiesNextPage.current = false;
    }
  };

  /**
   * Drops studies from the table when a delete, abort or restore socket event arrives. Clones the studies, removes every row matching each payload entry by studyId or id, shows an aborted notice with the zero-padded friendly id for ABORTED entries, flags whether the thinned page needs topping up, and closes the live ECG and upload status modals when their displayed study was among the removed ones.
   *
   * @param data - Socket payload listing the studies to remove and their statuses.
   * @returns Nothing.
   */
  const handleDeleteStudyFromSocketIo = (data: any) => {
    setState((prevState) => {
      const newStudies = _.cloneDeep(prevState.studies);
      _.forEach(data, (d) => {
        /**
         * Holds the study rows pulled out of the list for a delete event. Removes from newStudies every entry whose id matches the event's studyId or id, so the extracted rows can be used for the abort message.
         *
         * @returns The removed study rows.
         */
        const removedStudies = _.remove(newStudies, (x) => d.studyId === x.id || d.id === x.id);
        if (d.status === STUDY_STATUS.ABORTED) {
          if (removedStudies.length > 0) {
            const studyFid = zeroPad(removedStudies[0].studyFid);
            showMessageSuccess({ content: t`Study ${studyFid} is aborted` });
          }
        }
      });

      const setStateObject = {
        studies: newStudies,
        needFetchMoreStudiesCurrentPage: newStudies.length > 0 && newStudies.length < 11,
      };

      /**
       * Reports whether the delete event covers the study streaming in the live-ECG modal. Tests the event payload for any entry whose id or studyId equals displayedLiveEcgModalStudy?.id.
       *
       * @returns True when the open live-ECG modal must be closed.
       */
      const shouldCloseLiveEcgModal = _.some(
        data,
        (x) =>
          x.id === prevState.displayedLiveEcgModalStudy?.id ||
          x.studyId === prevState.displayedLiveEcgModalStudy?.id,
      );
      if (shouldCloseLiveEcgModal) {
        _.assign(setStateObject, {
          openLiveEcgModal: false,
          displayedLiveEcgModalStudy: null,
        });
      }

      /**
       * Reports whether the delete event covers the study shown in the upload-status modal. Tests the event payload for any entry whose studyId or id equals displayedUploadStatusModalStudy?.id.
       *
       * @returns True when the open upload-status modal must be closed.
       */
      const isUploaded = _.some(
        data,
        (x) =>
          x.studyId === prevState.displayedUploadStatusModalStudy?.id ||
          x.id === prevState.displayedUploadStatusModalStudy?.id,
      );
      if (isUploaded) {
        _.assign(setStateObject, {
          isOpenUploadStatusModal: false,
          displayedUploadStatusModalStudy: null,
        });
      }

      return setStateObject;
    });
  };

  /**
   * Attaches a newly generated ECG evaluation strip to its study row. Clones the studies, finds the row whose id equals the event studyId, assigns the whole message as lastEvaluationEvent and commits the clone.
   *
   * @param msg - Socket payload describing the new evaluation strip and its studyId.
   * @returns Nothing.
   */
  const handleNewEvaluationStripSocket = (msg: NewEvaluationStripSocketMessage) => {
    const studies = _.cloneDeep(state.studies);
    /**
     * Holds the study row the new evaluation strip belongs to. Searches the cloned studies for the entry whose id equals msg.studyId.
     *
     * @returns The matching study row, or undefined when the study is not listed.
     */
    const ele = _.find(studies, (x) => x.id === msg.studyId);
    if (ele) {
      _.assign(ele, { lastEvaluationEvent: msg });
      setState({ studies });
    }
  };

  /**
   * Refreshes a study row when a BioFlux Direct shipment event arrives over the socket. Deep-clones the studies in state, locates the row whose biofluxDirect.id matches the event's biodirectId, then re-fetches that study's BioFlux Direct record and merges the new status and shipment label into the row, leaving state untouched when no row matches.
   *
   * @param msg - Socket payload carrying the studyId and biodirectId of the shipment that changed.
   * @returns Nothing.
   */
  const handleBiodirectEventSocket = (msg: BiodirectEventSocketMessage) => {
    (setState as any)(async (prevState: any) => {
      const { studyId, biodirectId } = msg;

      const newStudies = _.cloneDeep(prevState.studies);

      /**
       * Holds the study row that owns the BioFlux Direct shipment named by the event. Searches the cloned studies for the entry whose biofluxDirect.id equals biodirectId.
       *
       * @returns The matching study row, or undefined when the shipment belongs to no listed study.
       */
      const ele = _.find(newStudies, (x) => x.biofluxDirect?.id === biodirectId);
      if (ele) {
        // *: -. None -> New
        // ----> Display "Biodirect ... processing"
        // *: 0. New -> Pending: update status
        // ----> Delete "Biodirect ... processing"
        // ----> transactionStatus: "Biodirect ... being shipped"
        // *: 1. Pending -> Running: update status
        // ----> Delete "Biodirect ... being shipped"
        // *: 2. Pending|Running -> Completed: update status
        // ----> Delete "Biodirect ... shipping"
        // ----> transactionStatus: "Biodirect ... being returned"
        // *: 3. Completed -> Returned: update status
        // ----> Delete "Biodirect ... being returned"

        // *: Fetch API to load new Biodirect status and shipment label status
        const biodirect = await fetchBiodirect(studyId);
        _.assign(ele, { ...biodirect });
        return { studies: newStudies };
      }

      return null;
    });
  };

  /**
   * Reloads the flagged studies list after a flag was addressed. Refetches with the cursor cleared in refresh mode so the already displayed range is rebuilt, then refreshes the ongoing-count badge when that handler was supplied.
   *
   * @returns Nothing.
   */
  const handleUpdateFlaggedStudies = () => {
    fetchData({
      updatedFilterObject: {
        cursor: undefined,
      },
      isRefresh: true,
    });
    if (handleFetchOngoingCount) {
      handleFetchOngoingCount();
    }
  };

  /**
   * Reconciles the flagged studies list with the outcome of a mark-as-addressed action. A failure empties the skip list, an id records that study so its own echoed socket update is ignored, and neither means the flagged list is reloaded outright.
   *
   * @param isFailed - Whether the mark-addressed request failed.
   * @param id - Study id whose self-triggered flag update should be skipped.
   * @returns Nothing.
   */
  const onUpdateMarkAddressed = (isFailed?: boolean, id?: string) => {
    if (isFailed) {
      skipSelfUpdateFlaggedStudies.current = [];
    } else if (id) {
      skipSelfUpdateFlaggedStudies.current.push(id);
    } else {
      handleUpdateFlaggedStudies();
    }
  };

  /**
   * Recomputes the elapsed-duration progress bar of every listed study. Deep-clones the given studies, sets progress to zero for rows still in STARTING status and to calculateStudyProgress otherwise, then commits the clone to state.
   *
   * @param studies - Study rows whose progress values should be recalculated.
   * @returns Nothing.
   */
  const handleUpdateActiveStudyProgress = (studies: FormattedStudy[]) => {
    const clonedStudies = _.cloneDeep(studies);
    _.forEach(clonedStudies, (study) => {
      study.progress = study.status === STUDY_STATUS.STARTING ? 0 : calculateStudyProgress(study);
    });
    setState({ studies: clonedStudies });
  };

  /**
   * Restarts the recurring recalculation of study progress bars. Clears and nulls any interval already stored on the ref, then schedules a new one that recomputes progress for the studies in state every 60 seconds.
   *
   * @returns Nothing.
   */
  const updateProgressInterval = () => {
    if (updateActiveStudyProgressIntervalFunc.current) {
      clearInterval(updateActiveStudyProgressIntervalFunc.current);
      updateActiveStudyProgressIntervalFunc.current = null;
    }
    updateActiveStudyProgressIntervalFunc.current = setInterval(() => {
      handleUpdateActiveStudyProgress(state.studies);
    }, 60000);
  };

  /**
   * Applies a new search or filter selection from the search bar. Suppresses app scrolling, merges the incoming filter into the current one, forces includeRma back on for a search reset, triggers a fetch with the merged filter and marks the table as loading on page one with the requested search-input disabled state.
   *
   * @param updatedFilterObject - Filter fields produced by the search component.
   * @param isDisabledSearchInput - Whether the search input stays disabled while loading.
   * @param isResetSearch - Whether the search was cleared rather than submitted.
   * @returns Nothing.
   */
  const handleChangeSearchComponent = (
    updatedFilterObject: FilterObject,
    isDisabledSearchInput: boolean,
    isResetSearch: boolean = false,
  ) => {
    preventAppScroll(true);
    _.assign(filterObject.current, { ...updatedFilterObject });
    if (isResetSearch) {
      filterObject.current.includeRma = true;
    }
    fetchData({ updatedFilterObject: filterObject.current });
    setState({
      isLoadedData: false,
      isDisabledSearchInput,
      studiesPage: 1,
    });
  };

  /**
   * Rebuilds the studies query filter from the current search box selection. Clones the active filter, clears the patientName, studyFid, studyFids, deviceId, patientId and cursor keys, then writes the trimmed search value into the single field chosen by the search-by selector (patient name, friendly id, patient id, else device id), forces includeRma on, applies the study type unless it is the all-types option, and stores the result back on the filter ref.
   *
   * @returns The rebuilt filter object, also persisted on the filter ref.
   */
  const handleCreateFilterObject = () => {
    const newFilterObject = _.cloneDeep(filterObject.current);
    _.assign(newFilterObject, {
      patientName: undefined,
      studyFid: undefined,
      studyFids: undefined,
      deviceId: undefined,
      patientId: undefined,
      cursor: undefined,
    });

    if (searchByValueProp === 'patientName') {
      newFilterObject.patientName = searchValueProp?.trim() || undefined;
    } else if (searchByValueProp === 'studyFid') {
      newFilterObject.studyFid = searchValueProp || undefined;
    } else if (searchByValueProp === 'patientId') {
      newFilterObject.patientId = searchValueProp?.trim() || undefined;
    } else {
      newFilterObject.deviceId = searchValueProp?.trim() || undefined;
    }

    newFilterObject.includeRma = true;

    newFilterObject.studyType = studyType !== 'All type' ? studyType : undefined;

    filterObject.current = newFilterObject;

    return newFilterObject;
  };

  /**
   * Starts creating a copy of an existing study, unless the facility is too far in arrears. When the user supports exactly one facility whose arrears equals 60 it emits the escalation popup event instead, otherwise it navigates to the study create route seeded with the source study id.
   *
   * @param studyId - Identifier of the study to copy into the create form.
   * @returns Nothing.
   */
  const onClickCopyStudy = (studyId: string) => {
    if (supportFacilities?.length === 1 && supportFacilities?.[0]?.arrears.toString() === '60') {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, supportFacilities?.[0]?.arrears);
    } else {
      navigate(`/studies/create/${studyId}`);
    }
  };

  /**
   * Opens the assign-device drawer for an unassigned study. Sets visibleAssignDeviceDrawer and stores the clicked study in shouldAssignDeviceStudy as the drawer's target.
   *
   * @param study - Study row that needs a device assigned.
   * @returns Nothing.
   */
  const handleAssignDeviceClick = (study: FormattedStudy) => {
    setState({
      visibleAssignDeviceDrawer: true,
      shouldAssignDeviceStudy: study,
    });
  };

  /**
   * Assigns a device with its study settings to an unassigned study from the drawer. Awaits the assign-device request, removes the now-assigned study from the cloned list, closes the drawer, flags whether the thinned page needs topping up and shows the success notice pointing at the Active tab, logging and surfacing the error on failure while always invoking the drawer callback.
   *
   * @param studyId - Study receiving the device.
   * @param deviceId - Device being assigned.
   * @param deviceType - Type of the assigned device.
   * @param studySetting - Recording thresholds and detection settings applied to the study.
   * @param callbackAssignDevice - Callback invoked once the request has settled, whatever the outcome.
   * @returns Promise that settles once the drawer has been updated and the callback fired.
   */
  const handleAssignDeviceDrawerClick = async (
    {
      studyId,
      deviceId,
      deviceType,
      studySetting,
    }: {
      studyId: string;
      deviceId: string;
      deviceType: string;
      studySetting: {
        duration: number;
        preEventTime: number;
        postEventTime: number;
        bradycardiaThreshold: number;
        tachycardiaThreshold: number;
        pauseLevel: number;
        pauseDetection: boolean;
        afibDetection: boolean;
        diagnosticLead: number;
        algAmp: number;
        ringId: string;
      };
    },
    callbackAssignDevice: () => void,
  ) => {
    try {
      const assignedDeviceStudy = await handleAssignDeviceToStudy({
        studyId,
        deviceId,
        deviceType,
        studySetting,
      });
      const clonedStudies = _.cloneDeep(state.studies);
      _.remove(clonedStudies, (x) => x.id === assignedDeviceStudy?.id);
      setState({
        visibleAssignDeviceDrawer: false,
        studies: clonedStudies,
        needFetchMoreStudiesCurrentPage: clonedStudies.length > 0 && clonedStudies.length < 11,
      });
      showMessageSuccess({
        content: t`Device assigned successfully. You can now find it in the Active tab.`,
      });
    } catch (error) {
      console.error('Failed to assign device to study: ', error);
      showMessageError({ content: (error as any)?.message });
    } finally {
      callbackAssignDevice();
    }
  };

  /**
   * Shows or hides the confirm-activity modal that guards an idle live ECG session. Writes the openConfirmActivityModal flag straight into the studies table state.
   *
   * @param openConfirmActivityModal - Whether the confirm-activity modal should be visible.
   * @returns Nothing.
   */
  const displayConfirmActivityModal = (openConfirmActivityModal: boolean) => {
    setState({ openConfirmActivityModal });
  };

  /**
   * Shows or hides the modal offering to resume a stopped live ECG stream. Writes the openStopLiveEcgModal flag into state and, when closing, also clears displayedLiveEcgModalStudy so no study stays attached to the modal.
   *
   * @param openStopLiveEcgModal - Whether the stopped-live-ECG modal should be visible.
   * @returns Nothing.
   */
  const displayStopLiveEcgModal = (openStopLiveEcgModal: boolean) => {
    setState({
      openStopLiveEcgModal,
      displayedLiveEcgModalStudy: !openStopLiveEcgModal ? null : state.displayedLiveEcgModalStudy,
    });
  };

  /**
   * Opens or closes the live ECG viewer for a given study. Writes both the openLiveEcgModal flag and the study whose trace should be rendered into the studies table state.
   *
   * @param openLiveEcgModal - Whether the live ECG modal should be visible.
   * @param displayedLiveEcgModalStudy - Study whose live ECG stream the modal should show.
   * @returns Nothing.
   */
  const displayViewLiveEcg = (
    openLiveEcgModal: boolean,
    displayedLiveEcgModalStudy?: FormattedStudy,
  ) => {
    setState({
      openLiveEcgModal,
      displayedLiveEcgModalStudy,
    });
  };

  /**
   * Confirms the user is still present during a live ECG session. Calls resetPause on the live ECG modal ref to restart its inactivity timer and closes the confirm-activity modal.
   *
   * @returns Nothing.
   */
  const onClickConfirmActivityModal = () => {
    viewLiveEcgModalRef.current?.resetPause();
    displayConfirmActivityModal(false);
  };

  /**
   * Ends the live ECG session when the idle-activity prompt is not answered. Closes the confirm-activity modal, closes the live ECG viewer for the displayed study and opens the stopped-live-ECG modal that offers to resume.
   *
   * @returns Nothing.
   */
  const handleStopStreaming = () => {
    displayConfirmActivityModal(false);
    displayViewLiveEcg(false, state.displayedLiveEcgModalStudy ?? undefined);
    displayStopLiveEcgModal(true);
  };

  /**
   * Resumes live ECG viewing after the user declined to stop the stream. Closes the stopped-live-ECG modal and reopens the live ECG viewer on the study already held in displayedLiveEcgModalStudy.
   *
   * @returns Nothing.
   */
  const handleResumeStreaming = () => {
    displayStopLiveEcgModal(false);
    displayViewLiveEcg(true, state.displayedLiveEcgModalStudy ?? undefined);
  };

  /**
   * Restores a deleted study and tracks the pending state on its row. Pushes the study id into restoreStudyIds so the row shows progress, awaits the restore request and shows a notice with the zero-padded friendly id or logs and surfaces the error, then removes the id from the pending list; the row itself leaves the list through the socket event.
   *
   * @param studyId - Study to restore.
   * @param studyFid - Friendly id used in the confirmation message.
   * @returns Promise that settles once the restore attempt has finished.
   */
  const onClickRestoreButton = async (studyId: string, studyFid: number) => {
    const clonedRestoreStudyIds = [...state.restoreStudyIds];
    clonedRestoreStudyIds.push(studyId);

    try {
      setState({ restoreStudyIds: clonedRestoreStudyIds });

      await handleRestoreStudy(studyId);
      showMessageSuccess({
        // content: t`Study ${zeroPad(studyFid)} is restored`,
        content: `Study ${zeroPad(studyFid)} is restored`,
      });
      // *: Restore studies via socket io
    } catch (error) {
      console.error('Failed to restore study: ', error);
      showMessageError({ content: (error as any)?.message });
    }

    _.remove(clonedRestoreStudyIds, (x) => x === studyId);
    setState({ restoreStudyIds: clonedRestoreStudyIds });
  };

  /**
   * Loads the next page of studies when the user scrolls to the bottom. Only acts while the tab is active and more data remains, setting the fetch-more flag and fetching from the stored cursor ref on the flagged tab or from the last loaded study's id elsewhere.
   *
   * @returns Nothing.
   */
  const useBottomScrollListener = () => {
    if (isActive && !state.isEndOfData) {
      needFetchMoreStudiesNextPage.current = true;
      if (isFlaggedStudiesTab) {
        fetchData({
          updatedFilterObject: {
            cursor: cursorObject.current.cursor,
          },
        });
      } else {
        fetchData({
          updatedFilterObject: {
            cursor: state.studies[state.studies.length - 1]?.id,
          },
        });
      }
    }
  };

  /**
   * Requests fresh ECG strips for a study from the get-ECG confirmation. Clears the confirmation state, awaits the evaluation-event request, shows the strips-available notice and starts the study card's polling for the last evaluation event, or surfaces the error message on failure.
   *
   * @param studyId - Study for which ECG evaluation strips are requested.
   * @returns Promise that settles once the request has been reported.
   */
  const onClickGetECG = async (studyId: string) => {
    setState({ isConfirmGetECG: null });
    try {
      await handleGetEvaluationEvent(studyId);
      showMessageSuccess({ content: t`ECG strips will be available in a minute` });

      (studyCardRefs.current as any)[studyId]?.handleGetLastEvaluationEventInterval(studyId);
    } catch (error) {
      showMessageError({ content: (error as any)?.message });
    }
  };

  /**
   * Handles sorting and pagination changes coming from the studies table. A changed sorter rewrites sortOrder, drops the cursor and refetches from page one; otherwise, when the requested page runs past the loaded rows and more data exists it sets the fetch-more flag, moves the cursor to the last loaded study and fetches only the missing rows, and when data is exhausted it either shows the no-more-studies notice or just stores the new page size and page.
   *
   * @param pagination - Requested page number and page size.
   * @param filters - Column filter values supplied by the table.
   * @param sorter - Requested sort direction, if any.
   * @returns Nothing.
   */
  const handleTableChange = (
    pagination: {
      current: number;
      pageSize: number;
    },
    filters: any,
    sorter: {
      order?: 'ascend' | 'descend';
    },
  ) => {
    const isChangedOrder = checkIsChangedSorter(sorter, filterObject.current, false);
    if (isChangedOrder) {
      const { order } = sorter;
      const sortOrder = !order || order === 'ascend' ? 'asc' : 'desc';
      filterObject.current.sortOrder = sortOrder;
      setState({
        isLoadedData: false,
        isDisabledSearchInput: true,
        studiesPage: 1,
      });
      filterObject.current.cursor = undefined;
      fetchData({ updatedFilterObject: filterObject.current });
      return;
    }

    const { current, pageSize } = pagination;
    if (current * pageSize > state.studies.length && !state.isEndOfData) {
      needFetchMoreStudiesNextPage.current = true;
      filterObject.current.cursor = state.studies[state.studies.length - 1].id;
      _.assign(state, {
        isLoadedData: false,
        isDisabledSearchInput: true,
        sizePerPage: pageSize,
        studiesPage: current,
        maxDisplayedStudies: pageSize * current,
      });
      fetchData({
        updatedFilterObject: filterObject.current,
        isRefresh: false,
        limit: pageSize * current - state.studies.length,
      });
    } else {
      const ceilLength = Math.ceil(state.studies.length / pageSize) * pageSize;
      if (current * pageSize > ceilLength && state.isEndOfData) {
        showMessageInfo({ content: t`No more studies` });
      } else {
        setState({
          maxDisplayedStudies: pageSize * current,
          sizePerPage: pageSize,
          studiesPage: current,
        });
      }
    }
  };

  /**
   * Toggles the troubleshooting solutions drawer for a study. Flips isOpenSolutionsModal and records which study the suggested solutions belong to.
   *
   * @param displayedSolutionStudy - Study the solutions drawer was opened from.
   * @returns Nothing.
   */
  const toggleDisplaySolutionsDrawer = (displayedSolutionStudy?: FormattedStudy) => {
    setState({
      isOpenSolutionsModal: !state.isOpenSolutionsModal,
      displayedSolutionStudy,
    });
  };

  /**
   * Toggles the patient disclaimer modal for a study. Flips isOpenDisclaimerModal and records which study the modal describes.
   *
   * @param displayedDisclaimerModalStudy - Study whose disclaimer should be shown.
   * @returns Nothing.
   */
  const toggleDisplayDisclaimerModal = (displayedDisclaimerModalStudy?: FormattedStudy) => {
    setState({
      isOpenDisclaimerModal: !state.isOpenDisclaimerModal,
      displayedDisclaimerModalStudy,
    });
  };

  /**
   * Toggles the ECG upload status modal for a study. Flips isOpenUploadStatusModal and records the study whose upload progress is displayed.
   *
   * @param displayedUploadStatusModalStudy - Study whose upload status the modal shows.
   * @returns Nothing.
   */
  const toggleDisplayUploadStatusModal = (displayedUploadStatusModalStudy?: FormattedStudy) => {
    setState({
      isOpenUploadStatusModal: !state.isOpenUploadStatusModal,
      displayedUploadStatusModalStudy,
    });
  };

  /**
   * Resends the patient reference code for the study held in the resend modal. Picks the success and failure wording from whether a code was already sent, awaits the send request, and on success closes the modal and stamps sentReferenceCodeAt on the matching study row, switches to the too-many-requests modal when the response asks the user to wait, and otherwise shows the failure message, always clearing the saving flag.
   *
   * @returns Promise that settles once the resend attempt has been reported.
   */
  const resendReferenceCode = async () => {
    const successfulMessage = getSendRefCodeSuccessfullyMessage(hasSentRefCode);
    const failedMessage = getSendRefCodeUnsuccessfullyMessage(hasSentRefCode);

    try {
      setState({ isSaving: true });

      // For customer environment
      // Non-null: resendReferenceCode only fires from the resend modal's right button, which is
      // reachable only after toggleDisplayResendModal set this study alongside opening it.
      const { id } = state.displayedResendRefCodeStudy!;
      const result = await handleSendStudyReferenceCode({ studyId: id });
      // *: For testing
      // const result = {
      //   isSuccess: true,
      // };

      if (result?.isSuccess) {
        showMessageSuccess({
          content: successfulMessage,
        });
        const setStateObject = { isOpenResendModal: false };
        const clonedStudies = _.cloneDeep(state.studies);
        /**
         * Holds the study row whose reference code was just resent. Searches the cloned studies for the entry whose id equals state.displayedResendRefCodeStudy.id so sentReferenceCodeAt can be stamped.
         *
         * @returns The matching study row, or undefined when the study is not listed.
         */
        const foundStudy = _.find(
          clonedStudies,
          (x) => x.id === state.displayedResendRefCodeStudy?.id,
        );
        if (foundStudy) {
          foundStudy.sentReferenceCodeAt = dayjs().toISOString();
          Object.assign(setStateObject, { studies: clonedStudies });
        }
        setState(setStateObject);
      } else if (_.includes(result?.message, 'before resending the reference code.')) {
        setState({
          isOpenResendModal: false,
          isOpenTooManyRequestModal: true,
          tooManyRequestMessage: result?.message ?? undefined,
        });
      } else {
        showMessageError({ content: failedMessage });
        setState({ isOpenResendModal: false });
      }
    } catch (error) {
      console.error('Failed to resend reference code: ', error);
      showMessageError({ content: failedMessage });
    } finally {
      setState({ isSaving: false });
    }
  };

  /**
   * Toggles the resend-reference-code modal for a study. Flips isOpenResendModal and records the study whose reference code would be resent.
   *
   * @param displayedResendRefCodeStudy - Study whose patient reference code the modal targets.
   * @returns Nothing.
   */
  const toggleDisplayResendModal = (displayedResendRefCodeStudy?: FormattedStudy) => {
    setState({
      isOpenResendModal: !state.isOpenResendModal,
      displayedResendRefCodeStudy,
    });
  };

  //* onClick common questions
  /**
   * Toggles the common-questions drawer from the studies table. Flips isVisibleCommonQuestionsDrawer against its current value in state.
   *
   * @returns Nothing.
   */
  const handleClickCommonQuestions = () => {
    setState({ isVisibleCommonQuestionsDrawer: !state.isVisibleCommonQuestionsDrawer });
  };

  /**
   * Toggles the do-not-show-again checkbox on the common-questions notice. Flips isCheck against its current value in state.
   *
   * @returns Nothing.
   */
  const onChangeCheckbox = () => {
    setState({ isCheck: !state.isCheck });
  };

  /**
   * Toggles whether aborted studies are included in the list. Mirrors the flipped value onto the persisted study navigation search state and flips isCheckedAbortedStudy in local state.
   *
   * @returns Nothing.
   */
  const onChangeCheckboxAbortedStudy = () => {
    _.assign(studyNavigationState.search, { isCheckedAbortedStudy: !state.isCheckedAbortedStudy });
    setState({ isCheckedAbortedStudy: !state.isCheckedAbortedStudy });
  };

  /**
   * Closes the common-questions notice popover and optionally remembers the choice. When the checkbox is ticked it persists the acknowledgement through user preferences, then clears isOpenNotiPopover.
   *
   * @returns Nothing.
   */
  const onClickConfirm = () => {
    if (state.isCheck) {
      userPreferences.setIsCheckedNotiCommonQuestions(true);
    }

    setState({ isOpenNotiPopover: false });
  };

  /**
   * Toggles the common-questions drawer visibility. Flips isVisibleCommonQuestionsDrawer in the studies table state.
   *
   * @returns Nothing.
   */
  const toggleDisplayDrawer = () => {
    setState({ isVisibleCommonQuestionsDrawer: !state.isVisibleCommonQuestionsDrawer });
  };

  /**
   * Clears the studies search while also dropping the aborted-studies filter. Resets isCheckedAbortedStudy on the persisted study navigation search state and then delegates to the shared clear handler.
   *
   * @returns Nothing.
   */
  const onClickClear = () => {
    _.assign(studyNavigationState.search, { isCheckedAbortedStudy: false });
    handleClickClear();
  };

  /**
   * Toggles the confirmation dialog for stopping an in-progress study. Flips isOpenConfirmStopStudyModal and stores the friendly id of the study to stop together with the pending command that must be handled.
   *
   * @param needStopStudyFid - Friendly id of the study the stop command targets.
   * @param pendingCommandToHandle - Pending device command already queued on that study.
   * @returns Nothing.
   */
  const toggleDisplayConfirmStopStudyModal = (
    needStopStudyFid?: number,
    pendingCommandToHandle?: string,
  ) => {
    setState({
      isOpenConfirmStopStudyModal: !state.isOpenConfirmStopStudyModal,
      needStopStudyFid,
      pendingCommandToHandle,
    });
  };

  /**
   * Sends the stop command for the study named by needStopStudyFid. Returns immediately when a stop is already pending and swaps to the unable-to-stop modal when an update command is pending, otherwise flags the matching row as isStopping, awaits the stop request and shows a notice with the zero-padded friendly id, while a pending-command-exists error opens the unable-to-stop modal and any other error is logged and surfaced.
   *
   * @returns Promise that settles once the stop attempt has been reported.
   */
  const stopStudy = async () => {
    if (state.pendingCommandToHandle === STUDY_PENDING_COMMAND.STOP_STUDY) {
      return;
    }

    if (state.pendingCommandToHandle === STUDY_PENDING_COMMAND.UPDATE_STUDY) {
      setState({
        isOpenConfirmStopStudyModal: false,
        isOpenUnableStopStudyModal: true,
      });
      return;
    }

    const clonedStudies = _.cloneDeep(state.studies);
    /**
     * Holds the study row queued for the stop-study command. Searches the cloned studies for the entry whose studyFid equals state.needStopStudyFid so the isStopping flag can be toggled on a copy.
     *
     * @returns The matching study row, or undefined when it is no longer listed.
     */
    const foundStudy = _.find(clonedStudies, (x) => x.studyFid === state.needStopStudyFid);
    if (_.isEmpty(foundStudy)) {
      return;
    }

    try {
      foundStudy.isStopping = true;
      setState({ studies: clonedStudies, isStopingStudyModal: true });
      await handleStopStudy(state.needStopStudyFid);
      const studyFid = zeroPad(state.needStopStudyFid);
      showMessageSuccess({ content: t`Study ${studyFid} is stopped` });
      setState({ isOpenConfirmStopStudyModal: false });
    } catch (error) {
      foundStudy.isStopping = false;
      const stateUpdate = {
        studies: clonedStudies,
      };
      if (
        (error as any)?.message === PENDING_COMMAND_EXISTS_MESSAGE ||
        _.includes(error as any, PENDING_COMMAND_EXISTS_MESSAGE)
      ) {
        _.assign(stateUpdate, {
          isOpenConfirmStopStudyModal: false,
          isOpenUnableStopStudyModal: true,
        });
      } else {
        console.error('Failed to stop study: ', error);
        showMessageError({ content: (error as any)?.message });
      }
      setState(stateUpdate);
    } finally {
      setState({ isStopingStudyModal: false });
    }
  };

  /**
   * Aborts the study queued in shouldAbortStudy and reflects the attempt in the table. Clones the studies to flag the target row as isAborting, returns early when that row is gone, calls the cancel-study request and reports success through the callback, or on failure logs the error, shows an error message, clears the isAborting flag and reports failure.
   *
   * @param callbackAbortStudy - Callback invoked with whether the abort request succeeded.
   * @returns Promise that settles once the abort attempt has been reported.
   */
  const handleAbortStudy = async (callbackAbortStudy: (isSuccess: boolean) => void) => {
    const clonedStudies = _.cloneDeep(state.studies);
    /**
     * Holds the clone of the study queued for abort. Looks up the entry in clonedStudies whose id matches state.shouldAbortStudy.id so the isAborting flag can be toggled on a copy.
     *
     * @returns The matching cloned study, or undefined when it is no longer listed.
     */
    const foundStudy = _.find(clonedStudies, (x) => x.id === state.shouldAbortStudy?.id);
    if (_.isEmpty(foundStudy)) {
      return;
    }

    try {
      foundStudy.isAborting = true;
      setState({ studies: clonedStudies });

      // For customer environment
      // Non-null: handleAbortStudy only fires from the abort modal's right button, which is
      // reachable only after handleAbortClick set this study alongside opening the modal.
      await handleCancelStudy(state.shouldAbortStudy!.id);
      callbackAbortStudy(true);
      // *: For testing (CP-2.29.0)
      // setTimeout(() => {
      //   foundStudy.isAborting = false;
      //   setState({ studies: clonedStudies });
      //   callbackAbortStudy(true);
      // }, 3000);
    } catch (error) {
      console.error('Failed to abort study: ', error);
      showMessageError({ content: (error as any)?.message });
      foundStudy.isAborting = false;
      setState({ studies: clonedStudies });
      callbackAbortStudy(false);
    }
  };

  /**
   * Opens the abort confirmation for a study row's abort action. Sets openConfirmAbortStudyModal and records the clicked study in shouldAbortStudy so the confirmation knows its target.
   *
   * @param study - Study row the user asked to abort.
   * @returns Nothing.
   */
  const handleAbortClick = (study: FormattedStudy) => {
    setState({
      openConfirmAbortStudyModal: true,
      shouldAbortStudy: study,
    });
  };

  /**
   * Dismisses the abort-study confirmation without aborting. Clears openConfirmAbortStudyModal and drops the pending shouldAbortStudy target from state.
   *
   * @returns Nothing.
   */
  const handleCancelClick = () => {
    setState({
      openConfirmAbortStudyModal: false,
      shouldAbortStudy: null,
    });
  };

  /**
   * Settles the abort-study confirmation once the abort request returns.
   * On success it closes the confirmation, drops the shouldAbortStudy target and clears
   * isCallingAbortStudy; on failure it only clears isCallingAbortStudy so the dialog stays open.
   *
   * @param isSuccess - Whether the study was aborted.
   * @returns Nothing.
   */
  const callbackAbortStudy = (isSuccess: boolean) => {
    if (isSuccess) {
      setState({
        openConfirmAbortStudyModal: false,
        shouldAbortStudy: null,
        isCallingAbortStudy: false,
      });
    } else {
      setState({ isCallingAbortStudy: false });
    }
  };

  /**
   * Runs the abort action behind the confirmation dialog's right button. Marks isCallingAbortStudy in state and triggers the abort of the queued study with the shared abort callback.
   *
   * @returns Promise that settles once the abort has been kicked off.
   */
  const handleRightButtonClick = async () => {
    setState({ isCallingAbortStudy: true });
    handleAbortStudy(callbackAbortStudy);
  };

  /**
   * Refreshes the last evaluation event shown for a single study. Fetches the study's last evaluation event, clones the studies in state, copies lastEvaluationEvent onto the matching row and commits it, logging the failure to the console when the request throws.
   *
   * @param id - Identifier of the study whose last evaluation event is requested.
   * @returns Promise that settles once state is updated or the error has been logged.
   */
  const handleGetLastEvaluationEvent = async (id: string) => {
    try {
      const study = await fetchLastEvaluationEvent(id);
      const clonedStudies = _.cloneDeep(state.studies);
      /**
       * Holds the study row whose last evaluation event was just fetched. Searches the cloned studies for the entry whose id equals the requested id.
       *
       * @returns The matching study row, or undefined when the study is not listed.
       */
      const foundStudy = _.find(clonedStudies, (x) => x.id === id);
      if (foundStudy) {
        foundStudy.lastEvaluationEvent = study?.lastEvaluationEvent;
        setState({ studies: clonedStudies });
      }
    } catch (error) {
      console.error('Failed to get evaluation event: ', error);
    }
  };

  /**
   * Merges an incoming study socket payload into the matching listed study. Clones the studies, finds the row by msg.studyId or msg.id, then copies over only the defined fields (upload progress, status, last history, start/stop, pending command, stopping and aborting flags, device id), recomputes duration from start and stop or falls back to initDuration on the ongoing and flagged tabs, recomputes progress when a stop time first appears, and commits the clone only when something changed.
   *
   * @param msg - Socket payload holding the study identifier plus the fields that changed.
   * @returns Nothing.
   */
  const handleUpdateStudyFromSocketIo = (msg: any) => {
    setState((prevState) => {
      const newStudies = _.cloneDeep(prevState.studies);
      /**
       * Holds the study row addressed by the socket update. Searches the cloned studies for the entry whose id equals msg.studyId or msg.id.
       *
       * @returns The matching study row, or undefined when the study is not listed.
       */
      const ele = _.find(newStudies, (x) => x.id === msg.studyId || x.id === msg.id);
      let shouldUpdate = false;
      if (ele) {
        if (!_.isNil(msg.progress)) {
          ele.ecgUploadProgress = msg.progress;
          shouldUpdate = true;
        }
        if (!_.isNil(msg.status)) {
          ele.status = msg.status;
          shouldUpdate = true;
        }
        if (!_.isNil(msg.lastStudyHistory)) {
          ele.lastStudyHistory = msg.lastStudyHistory;
          shouldUpdate = true;
        }
        if (!_.isEmpty(msg.start)) {
          ele.start = msg.start;
          shouldUpdate = true;
        }
        if (!_.isEmpty(msg.stop)) {
          ele.stop = msg.stop;
          shouldUpdate = true;
        }

        if (
          !_.isUndefined(msg.pendingCommand) &&
          !_.isEqual(ele.pendingCommand, msg.pendingCommand)
        ) {
          ele.pendingCommand = msg.pendingCommand;
          shouldUpdate = true;
        }

        if (isOngoing || isFlaggedStudiesTab) {
          if (msg.status === STUDY_STATUS.STARTED) {
            ele.duration = getStudyDuration(msg.start, msg.stop);
          } else {
            ele.duration = ele?.initDuration;
          }
          shouldUpdate = true;
        } else {
          ele.duration = getStudyDuration(msg.start, msg.stop);
          shouldUpdate = true;
        }

        if (!_.isNil(msg.isStopping) && !_.isEqual(ele.isStopping, msg.isStopping)) {
          ele.isStopping = msg.isStopping;
          shouldUpdate = true;
        }
        if (!_.isNil(msg.isAborting)) {
          ele.isAborting = msg.isAborting;
          shouldUpdate = true;
        }

        if (!_.isEmpty(msg.stop) && _.isEmpty(ele.stop)) {
          ele.duration = getStudyDuration(msg.start, msg.stop);
          ele.progress = [STUDY_STATUS.STARTING, STUDY_STATUS.DRAFT].includes(msg.status)
            ? 0
            : calculateStudyProgress(msg);
          shouldUpdate = true;
        }

        if (!_.isNil(msg.deviceId)) {
          ele.device.deviceId = msg.deviceId;
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        return { studies: newStudies };
      }

      return null;
    });
  };

  /**
   * Decides how a study status event changes the currently listed studies. It refetches the first page and ongoing count when an active study is not listed yet or still holds the placeholder device, removes the row and refreshes the count once a listed study leaves the active statuses on the ongoing, unassigned and flagged tabs (also closing its disclaimer modal), removes rows on the end-of-duration tab whose stop time has not passed, and otherwise merges the payload into the row.
   *
   * @param msg - Socket payload with the study's new status, stop time and device details.
   * @returns Nothing.
   */
  const handleStudyEventSocket = (msg: StudyEventSocketMessage) => {
    /**
     * Holds the study row addressed by the incoming study event. Searches the current state.studies for the entry whose id equals msg.studyId.
     *
     * @returns The matching study row, or undefined when the study is not listed.
     */
    const ele = _.find(state.studies, (x) => x.id === msg.studyId);

    const listStatusActive = [
      STUDY_STATUS.STARTING,
      STUDY_STATUS.STARTED,
      STUDY_STATUS.ONGOING,
      STUDY_STATUS.COMPLETED,
    ];

    const isStudyActive = listStatusActive.includes(msg.status);
    const isStudyEndOfUser =
      [STUDY_STATUS.ONGOING].includes(msg.status) && dayjs().isAfter(dayjs(msg.stop));

    if (
      (isOngoing || isUnassigned || isFlaggedStudiesTab) &&
      isStudyActive &&
      (!ele || (ele && ele.device.deviceId === '0000000000')) &&
      (msg.status === STUDY_STATUS.STARTING ||
        (msg.deviceType === DEVICE.ICOR && msg.status === STUDY_STATUS.ONGOING))
    ) {
      fetchData({
        updatedFilterObject: {
          cursor: undefined,
          resolvedAtCursor: undefined,
        },
        isRefresh: false,
      });
      if (handleFetchOngoingCount) {
        handleFetchOngoingCount();
      }
    } else if ((isOngoing || isUnassigned || isFlaggedStudiesTab) && !isStudyActive && ele) {
      // *: Hide disclaimer modal if study is completed
      if (state.isOpenDisclaimerModal) {
        if (state.displayedDisclaimerModalStudy?.id === msg.studyId) {
          setState({
            isOpenDisclaimerModal: false,
            displayedDisclaimerModalStudy: null,
          });
        }
      }

      if (handleFetchOngoingCount) {
        handleFetchOngoingCount();
      }
      handleDeleteStudyFromSocketIo([msg]);
    } else if (isEndOfDuration && !isStudyEndOfUser && ele) {
      handleDeleteStudyFromSocketIo([msg]);
    } else if (ele) {
      handleUpdateStudyFromSocketIo(msg);
    }
  };

  /**
   * Records a device's newly reported last-sync time on the study using it. Clones the studies, finds the row whose device.id or device.deviceId equals the event deviceId, writes device.lastSync from the payload and commits the clone.
   *
   * @param msg - Socket payload with the deviceId and its lastSync timestamp.
   * @returns Nothing.
   */
  const handleLastSyncSocket = (msg: LastSyncSocketMessage) => {
    const clonedStudies = _.cloneDeep(state.studies);
    /**
     * Holds the study row whose device reported the new last-sync time. Searches the cloned studies for the entry whose device.id or device.deviceId equals msg.deviceId.
     *
     * @returns The matching study row, or undefined when no listed study uses that device.
     */
    const ele = _.find(
      clonedStudies,
      (x) => x.device?.id === msg.deviceId || x.device?.deviceId === msg.deviceId,
    );
    if (ele) {
      ele.device.lastSync = msg.lastSync;
      setState({ studies: clonedStudies });
    }
  };

  /**
   * Propagates a device status change from the socket into the listed studies. Deep-clones the studies, writes the event status onto both device.status and deviceStatus of every row using the reported deviceId, and only commits the clone when at least one row matched.
   *
   * @param msg - Socket payload with the deviceId and its new status.
   * @returns Nothing.
   */
  const handleDeviceEventSocket = (msg: DeviceEventSocketMessage) => {
    setState((prevState) => {
      let isUpdate;
      const clonedStudies = _.cloneDeep(prevState.studies);
      _.forEach(clonedStudies, (d) => {
        if (d.device?.deviceId === msg.deviceId) {
          d.device.status = msg.status;
          d.deviceStatus = msg.status;
          isUpdate = true;
        }
      });

      if (isUpdate) {
        return { studies: clonedStudies };
      }

      return null;
    });
  };

  /**
   * Refreshes the open and total event counts of the studies named in an event message. Clones the studies, keeps only the ids that are currently listed, fetches their event counts in one request, copies openEvents and totalEvents onto the matching rows and commits the clone only when something changed, logging a fetch failure to the console.
   *
   * @param msg - Study ids whose event counts have to be re-read.
   * @returns Promise that settles once counts are applied or the error has been logged.
   */
  const handleUpdateTotalEvents = async (msg: string[]) => {
    const clonedStudies = _.cloneDeep(state.studies);
    const studyIds: string[] = [];
    const listStudy: {
      id: string;
      openEvents: number;
      totalEvents: number;
    }[] = [];
    _.forEach(msg, (d) => {
      /**
       * Holds the study row matching one id from the event-count message. Searches the cloned studies for the entry whose id equals the current message id.
       *
       * @returns The matching study row, or undefined when the id is not listed.
       */
      const ele = _.find(clonedStudies, (x) => x.id === d);
      if (ele) {
        studyIds.push(ele.id);
        listStudy.push(ele);
      }
    });
    if (studyIds.length > 0) {
      try {
        let isChange;
        const eventCount = await fetchEventCounts(studyIds);
        _.forEach(eventCount?.eventCounts, (d) => {
          /**
           * Holds the study row that a returned event-count entry belongs to. Searches the collected listStudy for the entry whose id equals the count record's study.id.
           *
           * @returns The matching study row, or undefined when it is no longer tracked.
           */
          const study = _.find(listStudy, (x) => x.id === d?.study?.id);
          if (study) {
            study.openEvents = d?.openEvents ?? 0;
            study.totalEvents = d?.totalEvents ?? 0;
            isChange = true;
          }
        });
        if (isChange) {
          setState({ studies: clonedStudies });
        }
      } catch (error) {
        console.error('Failed to fetch event counts: ', error);
      }
    }
  };

  /**
   * Applies a device-started-study socket event to the matching study row. Clones the studies, finds the row whose id equals the event studyId, copies over status and referenceCode when present, and commits the clone plus a refreshed displayedDisclaimerModalStudy when that row is the one open in the disclaimer modal.
   *
   * @param msg - Socket payload with the studyId plus its new status and reference code.
   * @returns Nothing.
   */
  const handleDeviceStartedStudySocket = (msg: DeviceStartedStudySocketMessage) => {
    setState((prevState) => {
      const setStateObject = {};
      let shouldUpdate = false;

      const clonedStudies = _.cloneDeep(prevState.studies);
      /**
       * Holds the study row targeted by the device-started-study event. Searches the cloned studies for the entry whose id equals msg.studyId.
       *
       * @returns The matching study row, or undefined when the study is not listed.
       */
      const foundStudy = _.find(clonedStudies, (x) => x.id === msg.studyId);
      if (foundStudy) {
        if (msg.status) {
          foundStudy.status = msg.status;
          shouldUpdate = true;
        }

        if (msg.referenceCode) {
          foundStudy.referenceCode = msg.referenceCode;
          shouldUpdate = true;
        }

        if (shouldUpdate) {
          Object.assign(setStateObject, { studies: clonedStudies });
        }

        if (foundStudy.id === prevState.displayedDisclaimerModalStudy?.id) {
          Object.assign(setStateObject, { displayedDisclaimerModalStudy: foundStudy });
        }
      }

      return setStateObject;
    });
  };

  /**
   * Updates a study's ECG upload status from an upload-status socket event. Clones the studies, finds the row whose id equals the event studyId, stores the message as uploadStatus and additionally refreshes displayedUploadStatusModalStudy when that row is the one currently open in the upload status modal.
   *
   * @param msg - Socket payload describing the upload progress of a study.
   * @returns Nothing.
   */
  const handleUploadStatusSocket = (msg: UploadStatusSockeMessage) => {
    setState((prevState: NullableStudiesTableState) => {
      const newStudies = _.cloneDeep(prevState.studies);
      /**
       * Holds the study row the upload-status event refers to. Searches the cloned studies for the entry whose id equals msg.studyId.
       *
       * @returns The matching study row, or undefined when the study is not listed.
       */
      const ele = _.find(newStudies, (x) => x.id === msg.studyId);
      const setStateObject = {};
      if (ele) {
        ele.uploadStatus = msg;
        _.assign(setStateObject, { studies: newStudies });
        if (ele.id === prevState.displayedUploadStatusModalStudy?.id) {
          _.assign(setStateObject, { displayedUploadStatusModalStudy: ele });
        }
      }
      return setStateObject;
    });
  };

  /**
   * Routes study-deleted socket events to the list removal depending on the active tab. On the deleted-studies tab it removes rows only for restore events, on every other tab only for genuine deletions, so a study leaves whichever list it no longer belongs to.
   *
   * @param msg - Socket payload entries carrying the restore flag and study identifiers.
   * @returns Nothing.
   */
  const handleStudyDeletedSocket = (msg: StudyDeletedSocketMessage[]) => {
    if (isDeleted && msg && msg.length > 0 && msg[0].restore) {
      // *: Tab Study Deleted + Restore study
      handleDeleteStudyFromSocketIo(msg);
    } else if (!isDeleted && msg && msg.length > 0 && !msg[0].restore) {
      // *: Other Tab Study + Delete study
      handleDeleteStudyFromSocketIo(msg);
    }
  };

  /**
   * Applies a study-updated socket event to the table, removing or patching the row. It closes the disclaimer modal once the patient first synced or the study was aborted, removes the row and refreshes the ongoing count when the study leaves the active statuses or a clinic physician lost it to another interpretting physician, and otherwise copies study note, RMA, pairing, sync, aborting and pending command fields and reloads the flagged list on a resolvedAt that was not self-triggered.
   *
   * @param msg - Socket payload with the changed study fields.
   * @returns Nothing.
   */
  const handleStudyUpdatedSocket = (msg: StudyUpdatedSocketMessage) => {
    const setStateObject = {};

    let shouldReloadFlaggedStudies = false;

    const clonedStudies = _.cloneDeep(state.studies);
    /**
     * Holds the study row addressed by the study-updated event. Searches the cloned studies for the entry whose id equals msg.studyId or msg.id.
     *
     * @returns The matching study row, or undefined when the study is not listed.
     */
    const ele = _.find(clonedStudies, (x) => msg.studyId === x.id || msg.id === x.id);
    const listStatusActive = [
      STUDY_STATUS.STARTING,
      STUDY_STATUS.STARTED,
      STUDY_STATUS.ONGOING,
      STUDY_STATUS.COMPLETED,
    ];
    const isStudyActive = listStatusActive.includes(msg.status);

    if (ele) {
      let shouldUpdate = false;
      if (state.isOpenDisclaimerModal) {
        if (
          (msg.patientFirstSyncDeviceAt || msg.status === STUDY_STATUS.ABORTED) &&
          state.displayedDisclaimerModalStudy?.id === msg.id
        ) {
          _.assign(setStateObject, {
            isOpenDisclaimerModal: false,
            displayedDisclaimerModalStudy: null,
          });
        }
      }

      if (msg.status === STUDY_STATUS.ABORTED || (!isStudyActive && msg.status)) {
        if (isOngoing || isUnassigned || isFlaggedStudiesTab) {
          if (handleFetchOngoingCount) {
            handleFetchOngoingCount();
          }
          handleDeleteStudyFromSocketIo([msg]);
        }
      } else if (msg?.info?.interprettingPhysician) {
        // Remove study from list if user is physician and study has been assigned to other interpretting physician
        if (
          auth.isClinicPhysician() &&
          ele.interprettingPhysician?.id !== msg.info.interprettingPhysician
        ) {
          if (isOngoing && handleFetchOngoingCount) {
            handleFetchOngoingCount();
          }
          handleDeleteStudyFromSocketIo([msg]);
        }
      } else {
        if (msg.studyNote !== undefined) {
          ele.studyNote = msg.studyNote;
          shouldUpdate = true;
        }

        if (msg.rma !== undefined) {
          ele.rma = msg.rma;
          shouldUpdate = true;
        }

        if (msg.patientFirstSyncDeviceAt !== undefined) {
          ele.patientFirstSyncDeviceAt = msg.patientFirstSyncDeviceAt;
          shouldUpdate = true;
        }

        if (msg.patientPaired !== undefined) {
          ele.patientPaired = msg.patientPaired;
          shouldUpdate = true;
        }

        if (
          msg.flaggedStudy?.resolvedAt &&
          !_.includes(skipSelfUpdateFlaggedStudies.current, msg.id)
        ) {
          shouldReloadFlaggedStudies = true;
        }

        if (!_.isNil(msg.isAborting)) {
          ele.isAborting = msg.isAborting;
          shouldUpdate = true;
        }

        if (!_.isUndefined(msg.pendingCommand)) {
          ele.pendingCommand = msg.pendingCommand;
          shouldUpdate = true;
        }

        if (shouldUpdate) {
          _.assign(setStateObject, { studies: clonedStudies });
        }

        if (ele.id === state.displayedDisclaimerModalStudy?.id) {
          _.assign(setStateObject, { displayedDisclaimerModalStudy: ele });
          shouldUpdate = true;
        }
      }
      if (shouldUpdate) {
        setState(setStateObject);
      }
    }
    if (shouldReloadFlaggedStudies && isFlaggedStudiesTab) {
      handleUpdateFlaggedStudies();
    }
  };

  useEffect(() => {
    if (isFlaggedStudiesTab) {
      setState({ isOpenNotiPopover: isActive && !userPreferences.isCheckedNotiCommonQuestions() });
    }
  }, [isActive]);

  useEffect(() => {
    if (
      isActive &&
      ((!state.isLoadedData && !state.isEndOfData) || hasChangedSearchValue.current)
    ) {
      preventAppScroll(true);
      if (isCompleted) {
        setState({ isCheckedAbortedStudy: studyNavigationState.search.isCheckedAbortedStudy });
      }
      let filterObject;
      if (hasChangedSearchValue.current) {
        filterObject = handleCreateFilterObject();
        setState({ isLoadedData: false }); // *: Display skeleton
      } else {
        filterObject = {
          cursor: undefined,
        };
      }
      fetchData({ updatedFilterObject: filterObject });
      hasChangedSearchValue.current = false;
    }
  }, [isActive]);

  useEffect(() => {
    if (isCompleted) {
      const clonedStudies = _.cloneDeep(state.studies);
      /**
       * Holds the aborted studies within the current cloned list. Filters clonedStudies down to the entries whose status equals STUDY_STATUS.ABORTED.
       *
       * @returns The cloned study rows in aborted status.
       */
      const abortedStudies = _.filter(
        clonedStudies,
        (item) => item.status === STUDY_STATUS.ABORTED,
      );
      /**
       * Holds the ids of the aborted studies. Maps the filtered abortedStudies to their id values.
       *
       * @returns The ids of the aborted study rows.
       */
      const abortedStudyIds = _.map(abortedStudies, (item) => item.id);
      setState({ nonSelectedStudyIds: abortedStudyIds });
    }
  }, [checkedStudyIds, state.studies, state.isLoadedData]);

  useEffect(() => {
    let newEvaluationStripListener: any;
    let biofluxEvent: any;
    if (isOngoing || isFlaggedStudiesTab) {
      newEvaluationStripListener = emitter.addListener(
        EMITTER_CONSTANTS.NEW_EVALUATION_STRIP,
        handleNewEvaluationStripSocket,
      );
      biofluxEvent = emitter.addListener(
        EMITTER_CONSTANTS.BIODIRECT_EVENT,
        handleBiodirectEventSocket,
      );
    }

    if (isOngoing || isFlaggedStudiesTab) {
      updateProgressInterval();
    }

    let artifactStudiesUpdatedListener: any;
    if (isFlaggedStudiesTab) {
      artifactStudiesUpdatedListener = emitter.addListener(
        EMITTER_CONSTANTS.ARTIFACT_STUDIES_UPDATED,
        handleUpdateFlaggedStudies,
      );
    }

    return (): any => {
      if (biofluxEvent) {
        biofluxEvent.remove();
      }
      if (newEvaluationStripListener) {
        newEvaluationStripListener.remove();
      }
      if (artifactStudiesUpdatedListener) {
        artifactStudiesUpdatedListener.remove();
      }

      if (updateActiveStudyProgressIntervalFunc.current) {
        clearInterval(updateActiveStudyProgressIntervalFunc.current);
      }
    };
  }, [
    state.studies,
    state.isOpenDisclaimerModal,
    state.displayedDisclaimerModalStudy,
    state.isOpenUploadStatusModal,
    state.displayedUploadStatusModalStudy,
  ]);

  useUpdateEffect(() => {
    const condition =
      searchByValue.current === 'studyFid'
        ? false
        : searchValueProp === searchValue.current &&
          (_.isNil(searchValueProp) || searchValueProp === '');
    if (condition) {
      searchByValue.current = searchByValueProp;
      return;
    }

    searchValue.current = searchValueProp;
    searchByValue.current = searchByValueProp;
    multiStudyFid.current = multiStudyFidProp;

    hasChangedSearchValue.current = true;

    if (isActive) {
      if (!searchValueProp || !multiStudyFidProp?.length) {
        state.isCheckedAbortedStudy = false;
        _.assign(studyNavigationState.search, { isCheckedAbortedStudy: false });
      }
      const filterObject = handleCreateFilterObject();
      handleChangeSearchComponent(filterObject, false, !searchValueProp);
      hasChangedSearchValue.current = false;
    }
  }, [searchValueProp, multiStudyFidProp]);

  useUpdateEffect(() => {
    if (supportFacilities?.length > 0 || facilitySelection.userSupportFacilities()?.length > 0) {
      if (isActive) {
        setState({
          isLoadedData: false,
          isDisabledSearchInput: true,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
          studiesPage: 1,
        });
        fetchData({
          updatedFilterObject: {
            cursor: undefined,
          },
        });
      } else {
        setState({
          isLoadedData: false,
          isEndOfData: false,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
        });
      }
    }
  }, [selectedFacility?.value, selectedTypeSeePatients?.value]); // *: Use props.selectedFacility.value instead of props.selectedFacility because of Redux new object returned

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  useUpdateEffect(() => {
    setState({ isLoadedData: false });
    fetchData({
      updatedFilterObject: {
        cursor: undefined,
      },
      isRefresh: false,
    });
  }, [state.isCheckedAbortedStudy]);

  useUpdateEffect(() => {
    if (state.needFetchMoreStudiesCurrentPage) {
      fetchData({
        updatedFilterObject: {
          cursor: state.studies[state.studies.length - 1].id,
        },
        shouldDisplayInfoMessage: false,
      });
    }
  }, [state.needFetchMoreStudiesCurrentPage]);

  useEmitter(EMITTER_CONSTANTS.STUDY_EVENT, handleStudyEventSocket, [
    state.studies,
    state.isOpenDisclaimerModal,
    state.displayedDisclaimerModalStudy,
  ]);

  useEmitter(EMITTER_CONSTANTS.LAST_SYNC, handleLastSyncSocket, [state.studies]);

  useEmitter(EMITTER_CONSTANTS.DEVICE_EVENT, handleDeviceEventSocket, [state.studies]);

  useEmitter(EMITTER_CONSTANTS.ECG_UPLOAD_PROCESS, handleUpdateStudyFromSocketIo, [state.studies]);

  useEmitter(EMITTER_CONSTANTS.UPDATE_TOTAL_EVENTS, handleUpdateTotalEvents, [state.studies]);

  useEmitter(EMITTER_CONSTANTS.DEVICE_STARTED_STUDY, handleDeviceStartedStudySocket, [
    state.studies,
    state.displayedDisclaimerModalStudy,
  ]);

  useEmitter(EMITTER_CONSTANTS.UPLOAD_STATUS, handleUploadStatusSocket, [
    state.studies,
    state.displayedUploadStatusModalStudy,
  ]);

  useEmitter(EMITTER_CONSTANTS.STUDY_DELETED, handleStudyDeletedSocket, [
    isDeleted,
    state.studies,
    state.displayedLiveEcgModalStudy,
    state.displayedUploadStatusModalStudy,
  ]);

  useEmitter(EMITTER_CONSTANTS.STUDY_UPDATED_EVENT, handleStudyUpdatedSocket, [
    state.studies,
    state.isOpenDisclaimerModal,
    state.displayedDisclaimerModalStudy,
    state.displayedLiveEcgModalStudy,
    state.displayedUploadStatusModalStudy,
    isFlaggedStudiesTab,
  ]);

  const shouldDisplayStudiesMatch =
    (facilitySelection.userSupportFacilities()?.length > 0 || supportFacilities?.length > 0) &&
    (multiStudyFidProp?.length > 0 || !!searchValueProp) &&
    state.isLoadedData;

  const shouldDisplaySelectedStudies =
    isCompleted && checkedStudyIds.length > 0 && state.isLoadedData;

  return {
    navigate,
    gotoRedux: studyNavigationState,
    updateActiveStudyProgressIntervalFunc,
    hasChangedSearchValue,
    filterObject,
    needFetchMoreStudiesNextPage,
    isPreventFetchMore,
    searchByValue,
    searchValue,
    multiStudyFid,
    lastQueryFilter,
    cursorObject,
    flaggedStudiesPage,
    viewLiveEcgModalRef,
    studyCardRefs,
    skipSelfUpdateFlaggedStudies,
    state,
    setState,
    hasSentRefCode,
    getCanViewFlaggedStudiesFacilityIds,
    studiesFormatter,
    fetchData,
    handleDeleteStudyFromSocketIo,
    handleNewEvaluationStripSocket,
    handleBiodirectEventSocket,
    handleUpdateFlaggedStudies,
    onUpdateMarkAddressed,
    handleUpdateActiveStudyProgress,
    updateProgressInterval,
    handleChangeSearchComponent,
    handleCreateFilterObject,
    onClickCopyStudy,
    handleAssignDeviceClick,
    handleAssignDeviceDrawerClick,
    displayConfirmActivityModal,
    displayStopLiveEcgModal,
    displayViewLiveEcg,
    onClickConfirmActivityModal,
    handleStopStreaming,
    handleResumeStreaming,
    onClickRestoreButton,
    useBottomScrollListener,
    onClickGetECG,
    handleTableChange,
    toggleDisplaySolutionsDrawer,
    toggleDisplayDisclaimerModal,
    toggleDisplayUploadStatusModal,
    resendReferenceCode,
    toggleDisplayResendModal,
    handleClickCommonQuestions,
    onChangeCheckbox,
    onChangeCheckboxAbortedStudy,
    onClickConfirm,
    toggleDisplayDrawer,
    onClickClear,
    toggleDisplayConfirmStopStudyModal,
    stopStudy,
    handleAbortStudy,
    handleAbortClick,
    handleCancelClick,
    callbackAbortStudy,
    handleRightButtonClick,
    handleGetLastEvaluationEvent,
    handleUpdateStudyFromSocketIo,
    handleStudyEventSocket,
    handleLastSyncSocket,
    handleDeviceEventSocket,
    handleUpdateTotalEvents,
    handleDeviceStartedStudySocket,
    handleUploadStatusSocket,
    handleStudyDeletedSocket,
    handleStudyUpdatedSocket,
    shouldDisplayStudiesMatch,
    shouldDisplaySelectedStudies,
  };
};
